<template>
  <Teleport to="body">
    <Transition name="overlay">
      <div 
        v-if="isOverlayOpen && overlayImage" 
        class="overlay"
        @click.self="closeOverlay"
        @keydown.esc="closeOverlay"
        tabindex="0"
        ref="overlayRef"
      >
        <div class="overlay__content">
          <!-- Close button -->
          <button 
            class="overlay__close"
            @click="closeOverlay"
            aria-label="Close image overlay"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path 
                d="M18 6L6 18M6 6L18 18" 
                stroke="currentColor" 
                stroke-width="2" 
                stroke-linecap="round" 
                stroke-linejoin="round"
              />
            </svg>
          </button>

          <!-- Image container -->
          <div class="overlay__image-container">
            <img 
              :src="overlayImage.url" 
              :alt="overlayImage.alt || 'Enlarged image'"
              class="overlay__image"
              @click.stop
            />
          </div>

          <!-- Caption -->
          <p v-if="overlayImage.caption" class="overlay__caption">
            {{ overlayImage.caption }}
          </p>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { onMounted, onUnmounted, ref, watch, nextTick } from 'vue'
import { useImageOverlay } from '../composables/useImageOverlay'

const { isOverlayOpen, overlayImage, closeOverlay } = useImageOverlay()
const overlayRef = ref(null)

// Handle escape key
const handleKeydown = (event) => {
  if (event.key === 'Escape' && isOverlayOpen.value) {
    closeOverlay()
  }
}

// Focus management
const focusOverlay = () => {
  if (overlayRef.value) {
    overlayRef.value.focus()
  }
}

onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
  if (isOverlayOpen.value) {
    focusOverlay()
  }
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
})

// Watch for overlay opening to focus
watch(isOverlayOpen, (isOpen) => {
  if (isOpen) {
    // Use nextTick to ensure the overlay is rendered
    nextTick(() => {
      focusOverlay()
    })
  }
})
</script>

<style lang="scss" scoped>
@use '../assets/scss/variables' as *;

.overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.9);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: $spacing-lg;
  backdrop-filter: blur(4px);

  &__content {
    position: relative;
    max-width: 95vw;
    max-height: 95vh;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  &__close {
    position: absolute;
    top: -$spacing-lg;
    right: -$spacing-lg;
    background: rgba(255, 255, 255, 0.9);
    border: none;
    border-radius: 50%;
    width: 48px;
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
    z-index: 10001;
    color: $xw-black;

    &:hover {
      background: rgba(255, 255, 255, 1);
      transform: scale(1.1);
    }

    &:focus {
      outline: 2px solid $xw-teal-light;
      outline-offset: 2px;
    }

    svg {
      width: 20px;
      height: 20px;
    }
  }

  &__image-container {
    display: flex;
    align-items: center;
    justify-content: center;
    max-width: 100%;
    max-height: calc(95vh - 100px); // Account for caption space
  }

  &__image {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    border-radius: 8px;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  }

  &__caption {
    margin-top: $spacing-md;
    color: white;
    text-align: center;
    font-size: 0.875rem;
    max-width: 600px;
    line-height: 1.4;
  }

  // Responsive adjustments
  @media (max-width: $breakpoint-md) {
    padding: $spacing-md;

    &__close {
      top: -$spacing-md;
      right: -$spacing-md;
      width: 40px;
      height: 40px;

      svg {
        width: 16px;
        height: 16px;
      }
    }

    &__image-container {
      max-height: calc(95vh - 80px);
    }
  }
}

// Transition animations
.overlay-enter-active,
.overlay-leave-active {
  transition: all 0.3s ease;
}

.overlay-enter-from,
.overlay-leave-to {
  opacity: 0;
}

.overlay-enter-to,
.overlay-leave-from {
  opacity: 1;
}

// Prevent body scroll when overlay is open
:global(body.overlay-open) {
  position: fixed;
  overflow: hidden;
  width: 100%;
}
</style> 